import { useRef } from "react";

// Segmented 6-box OTP entry. Renders `length` individual boxes that fill the row,
// with auto-advance, backspace-to-previous, arrow-key nav, and paste support.
// The boxes are merged into a single string via onChange, so callers keep using
// one value (e.g. sent straight to the API) — no change to their submit logic.
export const OtpInput = ({ value = "", onChange, length = 6, autoFocus = false }) => {
  const inputsRef = useRef([]);
  const chars = Array.from({ length }, (_, i) => value[i] || "");

  const emit = (arr) => onChange(arr.join("").slice(0, length));

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      const next = chars.slice();
      next[i] = "";
      emit(next);
      return;
    }
    // Typing (or pasting) one or more digits starting at box i.
    const next = chars.slice();
    let idx = i;
    for (const c of raw) {
      if (idx >= length) break;
      next[idx] = c;
      idx += 1;
    }
    emit(next);
    inputsRef.current[Math.min(idx, length - 1)]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = chars.slice();
      if (chars[i]) {
        next[i] = "";
        emit(next);
      } else if (i > 0) {
        next[i - 1] = "";
        emit(next);
        inputsRef.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      inputsRef.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const raw = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!raw) return;
    onChange(raw);
    inputsRef.current[Math.min(raw.length, length - 1)]?.focus();
  };

  return (
    // Single-box look: one bordered container, seamless cells with faint dividers.
    <div
      className="flex w-full h-12 bg-white border border-gray-300 rounded-lg overflow-hidden transition focus-within:ring-2 focus-within:ring-jiojbm40 focus-within:border-transparent"
      onPaste={handlePaste}
    >
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoFocus={autoFocus && i === 0}
          value={c}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          placeholder="*"
          className="flex-1 min-w-0 h-full bg-transparent text-center text-xl font-mono focus:outline-none"
        />
      ))}
    </div>
  );
};
